/**
 * VatReserveTile — Cockpit-Kachel „USt-Rücklage" (M-CFO-2, `architecture-cockpit-cfo.md` §8.2).
 *
 * Rendert die fail-closed Zustandsmaschine {@link deriveVatReserveView}. FACHLICH/
 * RECHTLICH SENSIBEL: ALLE Nutzertexte kommen 1:1 aus den Jurist-Wortlauten
 * (`jurist-ustrücklage-wortlaute.md`) über i18n-Keys `cockpit:cfo.vatReserve.*` —
 * die Komponente formuliert NICHTS um. Bindende Auflagen:
 *   1. NIE das Betragslabel „geschuldete USt" — nur „Leg rund … zurück"/„USt-Rücklage".
 *   2. §19-Kleinunternehmer erhalten NIE einen Betrag oder eine „keine USt"-Aussage;
 *      nur Nicht-Anzeige (`null`) oder der neutrale §13b-Hinweis. Diese Entscheidung
 *      trifft {@link deriveVatReserveView} — der §19-Ast steht vor der Betragslogik.
 *
 * Read-Ökonomie (§8.2): der +1 invoices-Read (`loadVatIncomeForUser`) fällt NUR im
 * Soll-Nicht-KU-Pfad an. §19/configure/ist rendern ohne Einnahmen-Read; die
 * §13b-Erkennung nutzt allein die bereits gebubbelten `expenses` (0 Reads).
 *
 * Kachel-Isolation (§11): Loader-Fehler ⇒ isolierter Fehlerzustand + Retry, nie
 * ein Render-Throw. Die Kachel crasht nie die Seite oder andere Kacheln.
 *
 * @module components/cockpit/VatReserveTile
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconVat } from '../../icons';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Spinner } from '../common/Spinner';
import { Collapsible } from '../common/Collapsible';
import logger from '../../utils/logger';
import { formatCurrency } from '../../utils/formatters';
import { centsToEuros } from '../../utils/moneyUtils';
import { currentQuarterPeriod } from '../../utils/expenseSummary';
import { computeNextUstvaDeadline } from '../../utils/ustvaDeadline';
import { buildVatReturn, toVatInputAggregate, type VatReturnFigures } from '../../utils/vatReturn';
import { deriveVatReserveView, type VatReserveView } from '../../utils/vatReserve';
import { loadVatIncomeForUser } from '../../loaders/cockpitLoaders';
import type { VatIncomeEntry } from '../../utils/vatReturn';
import type { Expense } from '../../types/expenseTypes';

/** Today's date as ISO YYYY-MM-DD (local timezone) — bewusst dupliziert, Muster `CockpitPage.tsx`. */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Konservative Anzeige-Rundung auf volle Euro AUFWÄRTS (§8.2) — Rohwert bleibt im View. */
function ceilToEuroCents(cents: number): number {
  return Math.ceil(cents / 100) * 100;
}

type IncomeLoadState =
  | { status: 'loading' }
  | { status: 'ready'; income: VatIncomeEntry[] }
  | { status: 'error' };

/**
 * Kachel-isolierter Einnahmen-Loader für den Soll-Pfad. `enabled=false` ⇒ kein
 * Fetch (der Read fällt nur bei offenen Gates an, §8.2). Fehler werden geloggt und
 * führen zu `status:'error'` — nie zu einem geworfenen Render-Fehler.
 */
function useVatIncomeLoad(
  enabled: boolean,
  scopeUid: string,
): { state: IncomeLoadState; retry: () => void } {
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState<IncomeLoadState>({ status: 'loading' });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState({ status: 'loading' });
    loadVatIncomeForUser(scopeUid)
      .then((income) => {
        if (!cancelled) setState({ status: 'ready', income });
      })
      .catch((err) => {
        if (!cancelled) setState({ status: 'error' });
        logger.error('VatReserveTile: Einnahmen-Ladefehler', err);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, scopeUid, retryToken]);

  return { state, retry: () => setRetryToken((n) => n + 1) };
}

/** Props: nur der `sellerData`-Ausschnitt + gebubbelte Ausgaben (§8.2 „Props"). */
export interface VatReserveTileProps {
  /** `effectiveScopeUid` aus `useAuth()` — Lade-Parameter für den Soll-Pfad. */
  scopeUid: string;
  /** Gebubbelte Ausgaben aus der Zone-A-Kachel; null = noch nicht geladen. */
  expenses: Expense[] | null;
  /** true ⇒ Ausgaben-Load fehlgeschlagen (§13b-Erkennung fail-closed ⇒ Nicht-Anzeige). */
  expensesError: boolean;
  /** `sellerData.isSmallBusiness` (§19). undefined ⇒ Nicht-KU-Pfad. */
  isSmallBusiness: boolean | undefined;
  /** `sellerData.taxationMode`. undefined ⇒ configure (KEINE Zahl). */
  taxationMode: 'soll' | 'ist' | undefined;
}

/**
 * USt-Rücklage-Kachel. Der Render-Zweig richtet sich strikt nach
 * {@link deriveVatReserveView} — kein Kachel-internes Umgehen der Gate-Kaskade.
 */
const VatReserveTile: React.FC<VatReserveTileProps> = ({
  scopeUid,
  expenses,
  expensesError,
  isSmallBusiness,
  taxationMode,
}) => {
  const { t, i18n } = useTranslation('cockpit');
  const navigate = useNavigate();

  // Render-Zeit-Read (nicht Modul-Const): CRA inlined `process.env.REACT_APP_*`
  // im Prod-Build; im Test wird der Wert je Fall frisch gelesen (§13 Testbarkeit).
  const ustvaEnabled = process.env.REACT_APP_USTVA_ENABLED === 'true';

  const today = todayISO();
  const period = currentQuarterPeriod(today);
  const deadline = computeNextUstvaDeadline(today);

  // +1 invoices-Read NUR im Soll-Nicht-KU-Pfad. Hook wird unbedingt aufgerufen
  // (Rules of Hooks), lädt aber nur bei `enabled` — §19/configure/ist ⇒ kein Read.
  const needsIncomeLoad = ustvaEnabled && isSmallBusiness !== true && taxationMode === 'soll';
  const { state, retry } = useVatIncomeLoad(needsIncomeLoad, scopeUid);

  // G2 — Kachel bleibt komplett aus (Engine M8 nicht freigegeben).
  if (!ustvaEnabled) return null;

  const goToUstva = () => navigate('/dashboard/umsatzsteuer');

  // ── Warn-Chips (fail-closed-Transparenz statt stiller Zahl) ──────────────────
  const renderWarnChips = (figures: VatReturnFigures | null): React.ReactNode => {
    if (figures === null) return null;
    const chips: React.ReactNode[] = [];
    if (figures.unclassifiedIncome.count > 0) {
      chips.push(
        <Badge key="unclassified" tone="warning">
          {t('cfo.vatReserve.warnUnclassified', { count: figures.unclassifiedIncome.count })}
        </Badge>,
      );
    }
    if (figures.excludedDraftVatOwedCount > 0) {
      chips.push(
        <Badge key="drafts" tone="warning">
          {t('cfo.vatReserve.warnDrafts', { count: figures.excludedDraftVatOwedCount })}
        </Badge>,
      );
    }
    if (chips.length === 0) return null;
    return <div className="mt-2 flex flex-wrap gap-1.5">{chips}</div>;
  };

  // ── Jurist-Disclaimer (kompakt sichtbar + ausführlich aufklappbar) ───────────
  const renderDisclaimer = (): React.ReactNode => (
    <>
      <p className="mt-2 text-xs text-secondary">{t('cfo.vatReserve.disclaimerCompact')}</p>
      <Collapsible title={t('cfo.vatReserve.disclaimerToggle')} className="mt-2">
        <p className="text-xs text-secondary">{t('cfo.vatReserve.disclaimerDetailed')}</p>
      </Collapsible>
    </>
  );

  // ── Countdown-Zeile (Reuse der bereits jurist-abgenommenen `tax.*`-Wortlaute) ─
  const renderCountdown = (deadlineISO: string | null, daysRemaining: number | null): React.ReactNode => {
    if (deadlineISO === null || daysRemaining === null) return null;
    const deadlineLabel = new Date(`${deadlineISO}T00:00:00Z`).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
    let text: string;
    if (daysRemaining > 0) text = t('tax.ustvaCountdown', { count: daysRemaining, deadline: deadlineLabel });
    else if (daysRemaining === 0) text = t('tax.ustvaCountdownDueToday', { deadline: deadlineLabel });
    else text = t('tax.ustvaCountdownOverdue', { deadline: deadlineLabel });
    return <p className="mt-2 text-xs text-secondary">{text}</p>;
  };

  // ── Kachel-Kopf (Icon + Titel), in allen sichtbaren Zuständen identisch ──────
  const header = (
    <div className="mb-2 flex items-center gap-2 text-secondary">
      <IconVat aria-hidden={true} />
      <h3 className="text-sm font-semibold">{t('cfo.vatReserve.title')}</h3>
    </div>
  );

  // ── Inhalt je View-Zustand (ohne Card-Rahmen) ────────────────────────────────
  const renderContent = (view: VatReserveView, figures: VatReturnFigures | null): React.ReactNode => {
    switch (view.kind) {
      case 'reserve':
        return (
          <>
            <p className="text-xl font-bold text-primary">
              {t('cfo.vatReserve.reserveAmount', {
                betrag: formatCurrency(centsToEuros(ceilToEuroCents(view.amountCents))),
              })}
            </p>
            <p className="mt-1 text-sm text-secondary">
              {t('cfo.vatReserve.reserveContext', { quartal: period.labelKey })}
            </p>
            {renderCountdown(view.deadlineISO, view.daysRemaining)}
            {renderWarnChips(figures)}
            {renderDisclaimer()}
          </>
        );
      case 'refund':
        return (
          <>
            <p className="text-xl font-bold text-primary">
              {t('cfo.vatReserve.refund', {
                betrag: formatCurrency(centsToEuros(ceilToEuroCents(view.amountCents))),
              })}
            </p>
            {renderWarnChips(figures)}
            {renderDisclaimer()}
          </>
        );
      case 'zero':
        return (
          <>
            <p className="text-sm text-primary">{t('cfo.vatReserve.zero')}</p>
            {renderWarnChips(figures)}
            {renderDisclaimer()}
          </>
        );
      case 'configure':
        return (
          <>
            <p className="text-sm text-primary">{t('cfo.vatReserve.configure')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => navigate('/dashboard/konto')}>
              {t('cfo.vatReserve.configureCta')}
            </Button>
          </>
        );
      case 'istPending':
        return (
          <>
            <p className="text-sm text-primary">{t('cfo.vatReserve.istPending')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={goToUstva}>
              {t('cfo.vatReserve.istPendingCta')}
            </Button>
          </>
        );
      case 'smallBusinessReverseCharge':
        return <p className="text-sm text-primary">{t('cfo.vatReserve.smallBusinessReverseCharge')}</p>;
      case 'hidden':
      default:
        return null;
    }
  };

  // ── Soll-Pfad: eigener Einnahmen-Read + Kachel-isolierter Lade-/Fehlerzustand ─
  if (needsIncomeLoad) {
    return (
      <Card
        role="button"
        tabIndex={0}
        onClick={goToUstva}
        onKeyDown={(e) => e.key === 'Enter' && goToUstva()}
        className="cursor-pointer"
      >
        {header}
        {state.status === 'loading' && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" />
          </div>
        )}
        {state.status === 'error' && (
          <div className="flex items-center justify-between gap-2">
            <span className="text-secondary">{t('empty.dash')}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                retry();
              }}
            >
              {t('retry')}
            </Button>
          </div>
        )}
        {state.status === 'ready' &&
          (() => {
            const figures = buildVatReturn(
              state.income,
              toVatInputAggregate(expenses ?? [], period),
              period,
              'soll',
              false,
            );
            const view = deriveVatReserveView({
              ustvaEnabled,
              isSmallBusiness,
              taxationMode,
              figures,
              deadline,
            });
            return renderContent(view, figures);
          })()}
      </Card>
    );
  }

  // ── Sync-Pfade (§19 / configure / istPending) — kein Einnahmen-Read ──────────
  // §13b-Erkennung allein aus den gebubbelten Ausgaben (0 Reads). expensesError
  // ⇒ expenses bleibt null ⇒ figures null ⇒ §19-Ast fällt fail-closed auf `hidden`.
  const figures =
    isSmallBusiness === true && expenses !== null && !expensesError
      ? buildVatReturn([], toVatInputAggregate(expenses, period), period, 'soll', true)
      : null;
  const view = deriveVatReserveView({ ustvaEnabled, isSmallBusiness, taxationMode, figures, deadline });

  if (view.kind === 'hidden') return null;

  return (
    <Card>
      {header}
      {renderContent(view, figures)}
    </Card>
  );
};

export default VatReserveTile;
